<script setup lang="ts">
import { computed } from 'vue'

defineOptions({
  name: 'VersionCard'
})

// Props pour la version - données complètes du grade
interface Props {
  gradeId: string
  fullName?: string
  motorization?: string
  battery?: string
  basePrice?: number
  thumbnail: string
  isActive?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  fullName: '',
  motorization: '',
  battery: '',
  basePrice: 0,
  isActive: false
})

// Événement d'émission
interface Emits {
  (e: 'select'): void
}

const emit = defineEmits<Emits>()

// Computed pour le titre séparé en deux parties
const titleParts = computed(() => {
  if (!props.fullName) return { vehicleModel: '', gradeName: '' }

  // Séparer "e VITARA" du reste
  const parts = props.fullName.split('e VITARA')
  return {
    vehicleModel: 'e VITARA',
    gradeName: parts[1]?.trim() || ''
  }
})

// Computed pour la description (motorization + battery) avec nowrap pour "roues"
const description = computed(() => {
  const moto = props.motorization.replace(/(\d+)\s+roues/, '<span class="nowrap">$1 roues</span>')
  return `${moto}<br>${props.battery}`
})

// Computed pour le prix formaté
const price = computed(() => {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0
  }).format(props.basePrice)
})

// Computed pour le chemin de l'image miniature
const thumbnailPath = computed(() => {
  const baseUrl = import.meta.env.BASE_URL

  // Grades Avantage/Privilège
  const avantagePrivilegeGrades = ['GRD_P10GL', 'GRD_P22GL', 'GRD_P22GLX']

  // Grades Style
  const styleGrades = ['GRD_P24GL', 'GRD_P24GLX']

  if (avantagePrivilegeGrades.includes(props.gradeId)) {
    return `${baseUrl}versions/e_vitara_avantage_privilege_thumbnail.png`
  }

  if (styleGrades.includes(props.gradeId)) {
    return `${baseUrl}versions/e_vitara_style_thumbnail.png`
  }

  // Fallback (ne devrait jamais être utilisé)
  return `${baseUrl}versions/default_car_thumbnail.png`
})

// Fonction pour sélectionner la version
function selectVersion() {
  emit('select')
}
</script>

<template>
  <div :class="['version-card', { 'version-card--active': isActive }]" @click="selectVersion">
    <img
      :src="thumbnailPath"
      :alt="fullName"
      class="version-thumbnail"
      loading="lazy"
    />
    <h3 class="version-title">
      <span class="vehicle-model">{{ titleParts.vehicleModel }}</span>
      <span class="grade-name">{{ titleParts.gradeName }}</span>
    </h3>
    <p class="version-desc" v-html="description"></p>
    <p class="version-price"><small>À partir de :</small><br>{{ price }}</p>
  </div>
</template>

<style scoped>
.version-card {
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: var(--spacing-xs);
  background-color: transparent;
  border: 1px solid transparent;
  color: var(--color-text-primary);
  cursor: pointer;
  transition: border-color 0.2s ease;
  max-width: 150px;
  text-align: left;
}

.version-card:hover {
  border-color: currentColor;
}

.version-card--active {
  border-color: currentColor;
}

.version-thumbnail {
  width: 150px;
  object-fit: contain;
  margin-bottom: var(--spacing-sm);
}

.version-title {
  margin: 0 0 var(--spacing-xs);
  font-size: 0.875rem;
  font-weight: 700;
  display: flex;
  flex-direction: column;
  line-height: 1.2;
}

.version-desc {
  margin: 0;
  font-size: var(--font-size-xs);
  border-block-end: 1px solid currentColor;
}

.version-desc .nowrap {
  white-space: nowrap;
}

.version-price {
  margin: 0 0 0 auto;
  padding-block-start: var(--spacing-xs);
  font-size: var(--font-size-md);
  font-weight: 700;
  line-height: 1;
}
.version-price small {
  font-weight: 400;
  font-size: var(--font-size-xs);
}
</style>

<style>
/* Style global pour le contenu injecté via v-html */
.nowrap {
  white-space: nowrap;
}
</style>
